import { useState, useEffect } from 'react'
import { Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { auditApi } from '../../lib/api'

const AuditLog = () => {
  const [logs, setLogs] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [jumpInput, setJumpInput] = useState('')
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const params: any = { page: currentPage, page_size: pageSize }
        if (typeFilter !== 'all') params.action = typeFilter
        if (searchTerm) params.keyword = searchTerm
        const resp = await auditApi.list(params) as any
        const rawItems = resp.logs || resp.items || resp || []
        const totalCount = resp.total ?? rawItems.length ?? 0
        setTotal(totalCount)
        setLogs(rawItems.map((l: any) => ({
          id: l.id,
          operatorName: l.operator_name || '系统',
          operatorId: l.operator_id || '',
          operatorRole: l.operator_role || '',
          action: l.action || 'unknown',
          target: l.target || '',
          targetId: l.target_id || '',
          details: l.details || '',
          pointsChange: l.points_change ?? null,
          ip: l.ip_address || l.ip || '-',
          createdAt: l.created_at ? new Date(l.created_at).toLocaleString('zh-CN') : '',
        })))
      } catch (e) {
        console.error('Failed to load audit logs', e)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [currentPage, typeFilter, pageSize, searchTerm])

  // 防抖搜索：搜索时重置到第 1 页
  useEffect(() => {
    const t = setTimeout(() => setCurrentPage(1), 400)
    return () => clearTimeout(t)
  }, [searchTerm])

  const getActionLabel = (action: string) => {
    const map: Record<string, string> = {
      login: '登录', login_fail: '登录失败', logout: '退出', create: '创建', update: '更新', delete: '删除',
      freeze_user: '冻结用户', unfreeze_user: '解冻用户',
      points_add: '积分增加', points_deduct: '积分扣减',
      exchange_confirm: '兑换确认', exchange_cancel: '兑换取消',
      exchange_create: '发起兑换', shipping: '发货', review: '审核',
      delete_point_record: '删除积分记录',
      cancel_point: '撤销积分'
    }
    return map[action] || action
  }

  const getActionColor = (action: string) => {
    if (action.includes('delete') || action.includes('cancel') || action.includes('deduct')) return 'bg-red-100 text-red-700'
    if (action.includes('create') || action.includes('add') || action.includes('confirm')) return 'bg-green-100 text-green-700'
    if (action.includes('update') || action.includes('shipping') || action.includes('freeze')) return 'bg-blue-100 text-blue-700'
    if (action.includes('login')) return 'bg-purple-100 text-purple-700'
    return 'bg-gray-100 text-gray-700'
  }

  // 页码数组
  const getPageNumbers = (): (number | string)[] => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1)
    const pages: (number | string)[] = [1]
    if (currentPage > 3) pages.push('...')
    const start = Math.max(2, currentPage - 1)
    const end = Math.min(totalPages - 1, currentPage + 1)
    for (let i = start; i <= end; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push('...')
    pages.push(totalPages)
    return pages
  }

  const handleJump = () => {
    const p = parseInt(jumpInput)
    if (!isNaN(p) && p >= 1 && p <= totalPages) {
      setCurrentPage(p)
      setJumpInput('')
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h1 className="text-xl font-bold text-gray-800">操作日志</h1>
        <p className="text-gray-500 text-sm mt-1">共 {total} 条记录</p>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder="搜索操作人、目标、详情..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { key: 'all', label: '全部' },
              { key: 'login', label: '登录' },
              { key: 'create', label: '创建' },
              { key: 'update', label: '更新' },
              { key: 'delete', label: '删除' },
              { key: 'freeze_user', label: '冻结' },
            ].map(f => (
              <button key={f.key} onClick={() => { setTypeFilter(f.key); setCurrentPage(1) }}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${typeFilter === f.key ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <span className="text-4xl mb-2">📋</span>
            <span>暂无日志记录</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">时间</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">操作人</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">角色</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">操作类型</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">目标</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">详情</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{log.createdAt}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-800">{log.operatorName}</div>
                      <div className="text-xs text-gray-400">{log.operatorId}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        log.operatorRole === 'admin' ? 'bg-purple-100 text-purple-700' :
                        log.operatorRole === 'medical' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {log.operatorRole === 'admin' ? '管理员' : log.operatorRole === 'medical' ? '医护' : log.operatorRole || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                        {getActionLabel(log.action)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div>{log.target}</div>
                      {log.targetId && <div className="text-xs text-gray-400 font-mono">{log.targetId.slice(0, 8)}...</div>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={log.details}>{log.details}</td>
                    <td className="px-6 py-4 text-sm text-gray-400 font-mono">{log.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 分页 */}
        {totalPages > 0 && (
          <div className="px-6 py-4 flex flex-wrap items-center justify-between gap-4 border-t">
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <span>共 {total} 条</span>
              <select
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}
                className="border border-gray-200 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
              >
                <option value={10}>10 条/页</option>
                <option value={20}>20 条/页</option>
                <option value={50}>50 条/页</option>
                <option value={100}>100 条/页</option>
              </select>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
                  <ChevronLeft size={16} />
                </button>
                {getPageNumbers().map((p, i) =>
                  p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>
                  ) : (
                    <button key={p} onClick={() => setCurrentPage(p as number)}
                      className={`min-w-[36px] px-2 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === p
                          ? 'bg-green-700 text-white'
                          : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}>
                      {p}
                    </button>
                  )
                )}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors">
                  <ChevronRight size={16} />
                </button>
                <div className="flex items-center gap-1 ml-2">
                  <span className="text-sm text-gray-400">跳至</span>
                  <input
                    type="text"
                    value={jumpInput}
                    onChange={(e) => setJumpInput(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                    onBlur={handleJump}
                    placeholder={String(currentPage)}
                    className="w-12 border border-gray-200 rounded-lg px-2 py-1 text-sm text-center focus:outline-none focus:ring-1 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-400">页</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AuditLog
